import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { notification, Select } from 'antd';
import { Button } from '@prio365/prio365-react-library';

import {
  getContactsIsFetching,
  getInternalProjectContactsIsFetching,
  RootReducerState,
} from '../../../apps/main/rootReducer';
import { Contact } from '../../../models/Contact';
import {
  ContactId,
  ProjectId,
  ContactType,
  OfficeId,
} from '../../../models/Types';
import { debounceFunction, distinct, isTemporaryId } from '../../../util';
import Flex from '../../../components/Flex';
import { useTranslation } from 'react-i18next';
import { apiSearchContacts } from '../api';
import PrioSpinner from '../../../components/PrioSpinner';
import { fetchInternalProjectContacts } from '../../projects/actions';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../../../theme/types';
import useContactsContext from '../hooks/useContactsProvider';
import useCompaniesContext from '../../companies/hooks/useCompaniesContext';
import useInternalProjectContactsContext from '../../projects/hooks/useInternalProjectContactsContext';
import { sortContactsHelper } from '../utils';

const setDebouncedSearchTerm = debounceFunction(
  (value: string, setSearchTerm: (value: string) => void) => {
    setSearchTerm(value);
  },
  500
);

const useContactPicker = (props: {
  projectId: ProjectId;
  officeId: OfficeId;
}) => {
  const { projectId, officeId } = props;

  const { contacts } = useContactsContext(true);

  const { getInternalProjectContactsContacts } =
    useInternalProjectContactsContext();

  return useMemo(() => {
    if (projectId) {
      return getInternalProjectContactsContacts(projectId);
    }

    if (officeId) {
      return contacts.filter(
        ({ officeId: _officeId, contactType }) =>
          officeId === _officeId && contactType === 'InternalContact'
      );
    }

    return contacts;
  }, [contacts, getInternalProjectContactsContacts, officeId, projectId]);
};

interface ContactPickerProps {
  value?: string | string[];
  label?: string;
  onChange?: (value: string | string[], companyId?: string) => void;
  onSelect?: (value: string, option: any) => void;
  onClick?: (e: React.MouseEvent<Element, MouseEvent>) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  excludedContactIds?: ContactId[];
  includedContacts?: Contact[];
  onlyInternalProject?: boolean;
  contactType?: ContactType;
  projectId?: ProjectId;
  officeId?: OfficeId;
  multiple?: boolean;
  fetch?: boolean;
  loading?: boolean;
  allowClear?: boolean;
  filter?: (contact: Contact) => boolean;
}

export const ContactPicker: React.FC<ContactPickerProps> = React.memo(
  (props) => {
    //#region ------------------------------ Defaults
    const {
      className,
      style,
      value,
      label,
      onChange,
      onSelect,
      onClick,
      disabled,
      excludedContactIds,
      onlyInternalProject: onlyInternal,
      contactType,
      projectId,
      officeId,
      multiple,
      loading,
      filter,
      includedContacts,
      allowClear,
    } = props;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const theme = useTheme<PrioTheme>();
    //#endregion

    //#region ------------------------------ States / Attributes / Selectors
    const _projectId = useMemo(() => {
      return onlyInternal ? projectId : undefined;
    }, [onlyInternal, projectId]);

    const _officeId = useMemo(() => {
      return contactType === 'InternalContact' ? officeId : undefined;
    }, [contactType, officeId]);

    const allContacts = useContactPicker({
      projectId: _projectId,
      officeId: _officeId,
    });

    const { getCompanyById } = useCompaniesContext();
    const { getContactById } = useContactsContext(true);

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [isOnline, setIsOnline] = useState<boolean>(false);

    const [onlineData, setOnlineData] = useState<Contact[]>([]);
    const [onlineLoading, setOnlineLoading] = useState<boolean>(false);

    const isFetching = useSelector<RootReducerState, boolean>((state) =>
      onlyInternal
        ? getInternalProjectContactsIsFetching(state)
        : getContactsIsFetching(state)
    );

    const contacts: Contact[] = useMemo(() => {
      const list = !isOnline
        ? allContacts.filter((contact) => contact.isArchived === false)
        : onlineData;
      if (contactType) {
        return list.filter((c) => {
          if (isOnline) {
            return c.contactType === null || c.contactType === contactType;
          }
          return c.contactType === contactType;
        });
      } else {
        return list;
      }
    }, [contactType, allContacts, onlineData, isOnline]);

    const contactsToShow = React.useMemo(() => {
      const contactIds = contacts.map((contact) => contact.contactId);
      const includedContactIds = (includedContacts ?? []).map(
        (contact) => contact.contactId
      );
      return distinct(
        contactIds
          .filter(
            (id) =>
              !isTemporaryId(id) &&
              (excludedContactIds ? !excludedContactIds.includes(id) : true) &&
              (!filter || filter(getContactById(id)))
          )
          .concat(includedContactIds)
      )
        .map((id) => {
          const contact = getContactById(id);
          if (!contact) {
            return null;
          }
          return {
            value: contact.contactId,
            title: `${contact?.firstName ?? ''} ${contact?.lastName ?? ''}`,
            label: (
              <>
                {`${contact.firstName} ${contact.lastName}`}
                {!onlyInternal && (
                  <span
                    style={
                      {
                        color:
                          'var(--prioCl-color-application-typography-muted)',
                        fontSize: 14,
                        display: '-webkit-box',
                        '-webkit-line-clamp': 2,
                        '-webkit-box-orient': 'vertical',
                        overflow: 'hidden',
                      } as CSSProperties
                    }
                  >
                    {getCompanyById(contact.companyId)?.fullName}
                  </span>
                )}
              </>
            ),
          };
        })
        .filter(Boolean)
        .sort(({ value: a }, { value: b }) =>
          sortContactsHelper(getContactById(a), getContactById(b))
        );
    }, [
      contacts,
      excludedContactIds,
      includedContacts,
      onlyInternal,
      filter,
      getContactById,
      getCompanyById,
    ]);
    //#endregion

    //#region ------------------------------ Methods / Handlers
    const handleOnChange: (value: string) => void = onChange
      ? (value: string) => {
          if (contactType === 'ExternalContact') {
            onChange(value ?? '', getContactById(value)?.companyId);
          } else {
            onChange(value ?? '');
          }
        }
      : null;

    const handleOnSearch = useCallback(
      (value: string) => {
        setSearchTerm(value);
        if (value === '') {
          if (isOnline) {
            setIsOnline(false);
          }
          if (onlineData.length > 0) {
            setOnlineData([]);
          }
        }
      },
      [isOnline, onlineData.length]
    );
    //#endregion

    //#region ------------------------------ Effects

    useEffect(() => {
      if (isOnline && searchTerm !== '' && searchTerm.length > 2) {
        const searchOnline = async () => {
          setOnlineLoading(true);
          const { result, data } = await apiSearchContacts(
            searchTerm,
            contactType
          );
          if (result.status >= 200 && result.status < 300) {
            setOnlineData(data);
          } else {
            notification.open({
              message: t('common:error'),
              description: t('contacts:errorMessages.fetchError'),
            });
          }
          setOnlineLoading(false);
        };
        searchOnline();
      }
    }, [searchTerm, isOnline, t, contactType]);

    useEffect(() => {
      if (projectId && onlyInternal) {
        dispatch(fetchInternalProjectContacts(projectId));
      }
    }, [projectId, onlyInternal, dispatch]);
    //#endregion

    return (
      <Select
        className={className}
        style={{
          ...(style ?? {}),
          ...(disabled || isFetching
            ? { backgroundColor: 'rgb(0,0,0,0.05)' }
            : {}),
        }}
        mode={multiple ? 'multiple' : null}
        showSearch
        value={value ?? (multiple ? [] : value)}
        placeholder={label}
        optionFilterProp="label"
        onChange={handleOnChange}
        onSelect={onSelect}
        onClick={onClick}
        onMouseDown={(e) => {
          if (multiple) {
            e.preventDefault();
          }
        }}
        filterOption={(input, option) => {
          return (option.title as string)
            .toString()
            .toLowerCase()
            .includes(input.toLowerCase());
        }}
        disabled={disabled || isFetching}
        loading={loading || isFetching}
        allowClear={allowClear}
        onSearch={(value: string) => {
          setDebouncedSearchTerm(value, handleOnSearch);
        }}
        dropdownRender={(menu) => (
          <Flex.Column>
            {onlineLoading && (
              <div
                style={{
                  background: '#FFFFFFBF',
                  position: 'absolute',
                  top: 0,
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 1,
                }}
              >
                <PrioSpinner />
              </div>
            )}
            {menu}
            {!isOnline && searchTerm !== '' && (
              <Button
                type="link"
                onClick={() => {
                  setIsOnline(true);
                }}
                iconProp={['fal', 'plus']}
                style={{
                  width: '100%',
                  borderTop: theme.old.borders.content,
                  paddingTop: 4,
                  height: 32,
                }}
              >
                {t('contacts:actions.showMore')}
              </Button>
            )}
          </Flex.Column>
        )}
        options={contactsToShow}
      />
    );
  }
);

export default ContactPicker;
